import { handle400Statuses, sendApiRequest } from '@services/api';
import { isUndefinedOrNull, logNetRequest } from './usefulFuncs';

import { unassignedDocGroupName } from '@constants/documents';

// Re-exported for the existing consumers that import it from here.
export { unassignedDocGroupName };

export function isNewsArticle(docType) {
  return docType.includes('News Article');
}

export function addFileDefaults(fileOrFiles, extraParams = {}) {
  const pdfDefaults = {
    do_table_extraction: true,
    do_vision_extraction: true,
  };
  const extractionOptionTypes = ['pdf', 'doc', 'docx'];
  if (Array.isArray(fileOrFiles)) {
    return fileOrFiles.map((f) => ({
      ...f,
      ...extraParams,
      extract_formula_dependencies: true,
      ...(extractionOptionTypes.includes(f.type) ? pdfDefaults : {}),
      docType: null,
      company: null,
    }));
  } else {
    return {
      ...fileOrFiles,
      ...extraParams,
      extract_formula_dependencies: true,
      ...(extractionOptionTypes.includes(fileOrFiles.type) ? pdfDefaults : {}),
      docType: null,
      company: null,
    };
  }
}

/* ******************************************************
  UTILITIES
****************************************************** */

export const filterDocsByTags = (documents, tags) => {
  const companyTags = tags.filter((tag) => tag.doc_tag_id !== null);
  const publicTags = tags.filter((tag) => tag.company_id !== null);
  const filtered = [];

  companyTags.forEach((tag) => {
    filtered.push(...documents.filter((doc) => doc.doc_tag?.id === tag.doc_tag_id));
  });

  publicTags.forEach((tag) => {
    const tagSymbol = tag.symbol ? String(tag.symbol).toUpperCase() : null;
    filtered.push(
      ...documents.filter((doc) => {
        if (doc.company_id === tag.company_id) return true;
        // Docs tagged to multiple companies list them all in plural_doc_tags, so a doc whose
        // primary company differs still matches when the selected company is one of its tags.
        return (doc.plural_doc_tags || []).some(
          (t) =>
            (t.company_id != null && t.company_id === tag.company_id) ||
            (t.ticker && tagSymbol && String(t.ticker).toUpperCase() === tagSymbol)
        );
      })
    );
  });

  return filtered;
};

export const filterDocsByReportingFilter = (documents, nextFilters) =>
  documents.filter((doc) => {
    let matches = true;
    if (nextFilters.quarter) {
      matches = matches && doc.fiscal_quarter === parseInt(nextFilters.quarter, 10);
    }
    if (nextFilters.month) {
      matches = matches && doc.fiscal_month === parseInt(nextFilters.month, 10);
    }
    if (nextFilters.year) {
      matches = matches && doc.fiscal_year === parseInt(nextFilters.year, 10);
    }
    return matches;
  });

export function parseDocGroup(doc) {
  const { security_symbol, security_name, doc_tag } = doc;

  if (security_symbol) {
    const symbolAndName = security_name ? security_symbol + ' - ' + security_name : security_symbol;
    return symbolAndName;
  }

  if (doc_tag) {
    const tagName = doc_tag.name;
    return tagName;
  }

  return unassignedDocGroupName;
}

export function filterTickerDocsFromDocs(docs, ticker) {
  if (!docs) return [];
  return docs.filter((doc) => doc.security_symbol !== ticker);
}

export function filterIsRecentDocs(docs, isUserDocs) {
  if (!docs) return [];
  if (isUserDocs) {
    return docs.filter((doc) => doc.is_recent);
  }
  return docs.filter((doc) => doc.is_recent && doc.added_by_user_id === null);
}

export function assembleDocumentTypes(docs) {
  if (!docs) return [];
  const documentTypes = docs.map((d) => (d.content_type === 'text/csv' ? 'CSV' : d.doc_type));
  return [...new Set(documentTypes)].sort();
}

export function isDocSelected(d, selectedDocs) {
  if (selectedDocs === null) {
    return false;
  }

  let isSelected = false;
  selectedDocs.forEach((selectedDoc) => {
    if (selectedDoc.id === d.id) {
      isSelected = true;
    }
  });
  return isSelected;
}

// The doc type used for grouping/filtering. Filings carry `display_category`,
// but some docs (e.g. news articles) only have `doc_type`, so fall back to it.
// Keeping bucketing and filtering on the same key prevents docs from one type
// (news) leaking into the results when another type is selected.
export function getDocCategory(d) {
  return d.display_category || d.doc_type;
}

export function extractDocTypes(docs) {
  if (!docs) return [];

  const longDocTypes = [];
  const shortDocTypes = [];
  const endOfList = [];
  docs.forEach((d) => {
    const lookAtType = getDocCategory(d);
    if (lookAtType === 'unknown') {
      !endOfList.includes('unknown') && endOfList.push(lookAtType);
    } else {
      if (lookAtType?.length > 7) {
        !longDocTypes.includes(lookAtType) && longDocTypes.push(lookAtType);
      } else {
        !shortDocTypes.includes(lookAtType) && shortDocTypes.push(lookAtType);
      }
    }
  });
  return [...shortDocTypes.sort(), ...longDocTypes.sort(), ...endOfList];
}

export function getDocTypeDisplayValue(docType) {
  if (!docType) return 'Other Materials';

  const documentTypeNames = {
    unknown: 'Other Materials',
  };
  return documentTypeNames[docType] || docType;
}

/* ******************************************************
  API SERVICES
****************************************************** */

export async function fetchNewsDocsByFilters(filters) {
  let URL = `v1/genai/documents?category=news&tickers=${filters.tickers.join(',').toUpperCase()}`;
  const { status, data } = await sendApiRequest('get', URL);
  logNetRequest(URL, status, data);
  handle400Statuses(status);
  if (status === 200) {
    return data?.documents || null;
  } else {
    return null;
  }
}

export async function fetchCompanyDocsByFilters(filters) {
  let URL = `v1/genai/documents?category=company`;
  if (filters.tickers) {
    URL += `&tickers=${filters.tickers.join(',').toUpperCase()}`;
  }
  if (filters.companyIds) {
    URL += `&company_ids=${filters.companyIds.join(',')}`;
  }
  const { status, data } = await sendApiRequest('get', URL);
  logNetRequest(URL, status, data);
  handle400Statuses(status);
  if (status === 200) {
    return data?.documents || null;
  } else {
    return null;
  }
}

export async function fetchCompanyTagDocsByFilters(filters) {
  let URL = `v1/genai/documents?category=company&doc_tag_ids=${filters.companyTagIds.join(',')}`;
  const { status, data } = await sendApiRequest('get', URL);
  logNetRequest(URL, status, data);
  handle400Statuses(status);
  if (status === 200) {
    return data?.documents || null;
  } else {
    return null;
  }
}

export async function fetchDocumentsForTags(tags) {
  // Route by whichever id the tag carries. Use a null+undefined-safe check so a
  // missing field never leaks through as `company_ids=undefined` (which the API
  // treats as no filter -> the entire document universe).
  const companyTagIds = tags.filter((tag) => !isUndefinedOrNull(tag.doc_tag_id)).map((tag) => tag.doc_tag_id);
  const publicTagIds = tags.filter((tag) => !isUndefinedOrNull(tag.company_id)).map((tag) => tag.company_id);

  let documents = [];

  // Helper to fetch and accumulate results
  async function fetchAndAccum(category, paramKey, ids) {
    if (!ids || !ids.length) return;
    await Promise.all(
      ids.map(async (id) => {
        const url = `v1/genai/documents?category=${category}&${paramKey}=${id}`;
        const response = await sendApiRequest('get', url);
        logNetRequest(url, response.status, response.data);
        handle400Statuses(response.status);
        if (response.status === 200 && response.data?.documents) {
          documents = documents.concat(response.data.documents);
        }
      })
    );
  }

  // Fetch by doc_tag_id from company and user categories
  await Promise.all([
    fetchAndAccum('company', 'doc_tag_ids', companyTagIds),
    fetchAndAccum('user', 'doc_tag_ids', companyTagIds),
    fetchAndAccum('company', 'company_ids', publicTagIds),
    fetchAndAccum('user', 'company_ids', publicTagIds),
  ]);

  return documents;
}

export async function fetchRecentDocumentsForTags(tags) {
  const documents = await fetchDocumentsForTags(tags);
  return documents.filter((doc) => doc.is_recent);
}

// News docs for the given tags. Kept separate from fetchDocumentsForTags because that
// function feeds other consumers (e.g. auto-select-recent) that should not pull in news.
// The news category keys uploaded/private tags on doc_tag_id and public companies on
// ticker (not company_id), mirroring the pre-consolidation fetchNewsCompanyDocs.
// All news docs are grouped under a single doc_type so they appear as one entry in the
// Document Types filter (instead of scattered across individual news sub-types). doc_type
// is only used for the selection filter UI — the query API keys on doc id — so this is safe.
export const NEWS_ARTICLES_DOC_TYPE = 'News Articles';

export async function fetchNewsDocumentsForTags(tags) {
  const docTagIds = tags.filter((tag) => !isUndefinedOrNull(tag.doc_tag_id)).map((tag) => tag.doc_tag_id);
  const tickers = tags
    .filter((tag) => isUndefinedOrNull(tag.doc_tag_id) && !isUndefinedOrNull(tag.symbol))
    .map((tag) => String(tag.symbol).toUpperCase());

  let documents = [];

  async function fetchAndAccum(paramKey, values) {
    if (!values || !values.length) return;
    await Promise.all(
      values.map(async (value) => {
        const url = `v1/genai/documents?category=news&${paramKey}=${value}`;
        const response = await sendApiRequest('get', url);
        logNetRequest(url, response.status, response.data);
        handle400Statuses(response.status);
        if (response.status === 200 && response.data?.documents) {
          documents = documents.concat(
            response.data.documents.map((doc) => ({ ...doc, doc_type: NEWS_ARTICLES_DOC_TYPE }))
          );
        }
      })
    );
  }

  await Promise.all([fetchAndAccum('doc_tag_ids', docTagIds), fetchAndAccum('tickers', tickers)]);

  return documents;
}
